import React, { useContext, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { LanguageNameSpaces, TradeGraphType } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import { closedTradesFilters } from "@shared/slices/filters.slice.ts";
import { serializeFilters } from "@shared/features/filters/utils.tsx";
import { selectDisplayCurrency } from "@shared/slices/common.slice.ts";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions.ts";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import OverviewPanel, {
  OVERVIEW_STACK_BREAKPOINT,
  OverviewChartZone,
  OverviewZone,
} from "common/NewLook/OverviewPanel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import useMoneyFormat from "features/trades/NewLook/useMoneyFormat.ts";
import TradesPieChart from "features/trades/TradesCharts/TradesPieChart.tsx";
import NumOfTradesChart from "features/trades/TradesCharts/NumOfTradesChart.tsx";
import TradesGraphCard from "features/trades/TradesCharts/TradesGraphCard.tsx";

interface NewLookTradesOverviewProps {
  isOpenTrades: boolean;
}

/**
 * The compact summary row above the trades list: allocation + number of
 * trades for open trades, and key figures + PnL charts for closed trades.
 */
const NewLookTradesOverview = ({ isOpenTrades }: NewLookTradesOverviewProps) =>
  isOpenTrades ? <OpenTradesOverview /> : <ClosedTradesOverview />;

export default React.memo(NewLookTradesOverview);

const OpenTradesOverview = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  return (
    <OverviewPanel
      allocationTitle={t("charts.amount_allocation")}
      allocation={<TradesPieChart bare={true} />}
      chart={<NumOfTradesChart flush={true} />}
    />
  );
};

const ClosedTradesOverview = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const filtersSliceState = useSelector(
    closedTradesFilters.selectors.selectFilters
  );
  const filters = useMemo(
    () => serializeFilters(filtersSliceState),
    [filtersSliceState]
  );

  return (
    <OverviewPanel variant="triple">
      <FiguresZone>
        <ClosedTradesFigures />
      </FiguresZone>
      <OverviewChartZone>
        <TradesGraphCard
          title={t("charts.accumulated_profit")}
          graphType={TradeGraphType.ACCUMULATING_PROFIT}
          precision={precision}
          filters={filters}
          flush={true}
        />
      </OverviewChartZone>
      <OverviewChartZone>
        <TradesGraphCard
          title={t("charts.profit_by_day")}
          graphType={TradeGraphType.PROFIT_BY_DAY}
          precision={precision}
          filters={filters}
          useBarChart={true}
          flush={true}
        />
      </OverviewChartZone>
    </OverviewPanel>
  );
};

const ClosedTradesFigures = () => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getTradesQuery, isLoading } = useContext(TradesPageContext)!;
  const { signedMoney, shown } = useMoneyFormat();
  const aggregatedData = getTradesQuery?.data?.aggregated_data;
  const change = aggregatedData?.change[displayCurrency] || 0;
  const loading = isLoading && !aggregatedData;

  return (
    <>
      <Figure>
        <FigureLabel>{t("statistics.closed_trades")}</FigureLabel>
        {loading ? (
          <Skeleton width={64} height={26} />
        ) : (
          <FigureValue>
            {(aggregatedData?.number_of_closed_trades || 0).toLocaleString()}
          </FigureValue>
        )}
      </Figure>
      <Figure>
        <FigureLabel>{t("statistics.total_change")}</FigureLabel>
        {loading ? (
          <Skeleton width={112} height={26} />
        ) : (
          <FigureValue style={{ color: directionColor(tokens, shown(change)) }}>
            {signedMoney(change)}
          </FigureValue>
        )}
      </Figure>
    </>
  );
};

// Key figures as the first zone: label, then the figure, centred in the
// zone's height (side by side once the zones stack).
const FiguresZone = styled(OverviewZone)`
  justify-content: center;
  gap: 24px;
  @media (max-width: ${OVERVIEW_STACK_BREAKPOINT}px) {
    flex-direction: row;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 16px 48px;
    padding-top: 20px;
  }
`;
const Figure = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 0;
`;
const FigureLabel = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const FigureValue = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
